import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/logo-only';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import { ADMIN_ROLES } from '../common/adminRoles';
import LightModeTheme from '../theme/LightModeTheme';
import {
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  NewPasswordPage,
  VerifyCodePage,
  PersonalLearningPage,
  PersonalGraphPage,
  UpNextPage,
  FlashcardReviewPage,
  NotificationsPage,
  LearningStage,
  PathwayBuilderPage,
  PathwayCreatePage,
  PathwaysPage,
  TopicEditSinglePage,
  ExplorePathwaysPage,
  ExplorePathwayViewPage,
  ExplorePathwayFollowingPage,
  ExplorePathwayFollowingViewerPage,
  ExplorePathwaySharedPage,
  ExplorePathwaySharedViewerPage,
  SuggestionsPage,
  CirclesPage,
  CircleCreatePage,
  CircleDetailsPage,
  CircleActivityPage,
  AIContentPage,
  ExternalPlatformsPage,
  UserAccountPage,
  ReportProblemPage,
  AdminPage,
  TopicMigrationPage,
  ResourceMigrationPage,
  LearningCyclesPage,
  LandingPage,
  TermsPage,
  PrivacyPolicyPage,
  ComingSoonPage,
  MaintenancePage,
  Page500,
  NotFoundPage
} from './pages';

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      element: (
        <LightModeTheme>
          <Outlet />
        </LightModeTheme>
      ),
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          )
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ResetPasswordPage />
            </GuestGuard>
          )
        },
        {
          path: 'new-password',
          element: (
            <GuestGuard>
              <NewPasswordPage />
            </GuestGuard>
          )
        },
        {
          path: 'verify',
          element: (
            <GuestGuard>
              <VerifyCodePage />
            </GuestGuard>
          )
        }
      ]
    },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/dashboard/overview" replace /> },
        {
          path: 'pathway',
          children: [
            { path: 'new', element: <PathwayCreatePage /> },
            { path: ':title', element: <PathwayBuilderPage /> },
            { path: ':title/:topicGuid', element: <PathwayBuilderPage /> },
            { path: 'edit/:pathwayId', element: <PathwayCreatePage /> }
          ]
        },
        {
          path: 'pathways',
          children: [{ path: 'view', element: <PathwaysPage /> }]
        },
        {
          path: 'explore',
          children: [
            { path: 'pathways', element: <ExplorePathwaysPage /> },
            { path: 'pathways/:slug', element: <ExplorePathwayViewPage /> },
            { path: 'following', element: <ExplorePathwayFollowingPage /> },
            { path: 'followed/:slug', element: <ExplorePathwayFollowingViewerPage /> },
            { path: 'sharedWithYou', element: <ExplorePathwaySharedPage /> },
            { path: 'shared/:slug', element: <ExplorePathwaySharedViewerPage /> },
            { path: 'suggestions', element: <SuggestionsPage /> },
            { path: 'ai-content', element: <AIContentPage /> }
          ]
        },
        {
          path: 'overview',
          children: [{ path: '', element: <PersonalLearningPage /> }]
        },
        {
          path: 'graph',
          children: [{ path: '', element: <PersonalGraphPage /> }]
        },
        { path: 'learning-stage', element: <LearningStage /> },
        { path: 'flashcards', element: <FlashcardReviewPage /> },
        { path: 'notifications', element: <NotificationsPage /> },
        {
          path: 'up-next',
          children: [{ path: '', element: <UpNextPage /> }]
        },
        { path: 'topic/:pathwaySlug/:topicGuid/view', element: <TopicEditSinglePage /> },
        {
          path: 'circle',
          children: [
            { path: '', element: <CirclesPage /> },
            { path: 'create', element: <CircleCreatePage /> },
            { path: 'activity', element: <CircleActivityPage /> },
            { path: ':circleId', element: <CircleDetailsPage /> },
            { path: ':circleId/ai-content', element: <AIContentPage /> }
          ]
        },
        { path: 'external-platforms', element: <ExternalPlatformsPage /> },
        {
          path: 'user',
          children: [
            { path: '', element: <Navigate to="/dashboard/user/account" replace /> },
            { path: 'account', element: <UserAccountPage /> },
            { path: 'report', element: <ReportProblemPage /> }
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: '',
              element: (
                <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
                  <AdminPage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'topic-migration',
              element: (
                <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
                  <TopicMigrationPage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'resource-migration',
              element: (
                <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
                  <ResourceMigrationPage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'learning-cycles',
              element: (
                <RoleBasedGuard accessibleRoles={ADMIN_ROLES}>
                  <LearningCyclesPage />
                </RoleBasedGuard>
              )
            }
          ]
        }
      ]
    },

    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFoundPage /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      // Landing page: GuestGuard wraps the layout so MainNavbar never mounts
      // for authenticated users or while auth is still initializing.
      path: '',
      element: (
        <GuestGuard>
          <MainLayout />
        </GuestGuard>
      ),
      children: [{ path: '', element: <LandingPage /> }]
    },
    {
      // Public info pages: no auth redirect needed.
      path: '',
      element: <MainLayout />,
      children: [
        { path: 'terms', element: <TermsPage /> },
        { path: 'privacy-policy', element: <PrivacyPolicyPage /> }
      ]
    }
  ]);
}
